/**
 * PBAC Capability Constants
 *
 * Action-oriented permissions that can be granted to roles.
 * Organized by scope: platform > org > project > assessment > report > test.
 */

// ── Platform-level capabilities ──
export const PLATFORM_MANAGE_SYSTEM_ADMINS = 'platform:manage_system_admins';
export const PLATFORM_MANAGE_ORGANIZATIONS = 'platform:manage_organizations';
export const PLATFORM_MANAGE_SYSTEM_POLICIES =
  'platform:manage_system_policies';

// ── Organization-level capabilities ──
export const ORG_MANAGE_MEMBERS = 'org:manage_members';
export const ORG_MANAGE_POLICIES = 'org:manage_policies';
export const ORG_MANAGE_SETTINGS = 'org:manage_settings';
export const ORG_VIEW_ACTIVITY_LOG = 'org:view_activity_log';
export const ORG_CREATE_PROJECT = 'org:create_project';
export const ORG_VIEW_ALL_PROJECTS = 'org:view_all_projects';

// ── Project-level capabilities ──
export const PROJECT_MANAGE_MEMBERS = 'project:manage_members';
export const PROJECT_ARCHIVE = 'project:archive';
export const PROJECT_MANAGE_ASSESSMENTS = 'project:manage_assessments';

// ── Assessment-level capabilities ──
export const ASSESSMENT_ANSWER = 'assessment:answer';
export const ASSESSMENT_SUBMIT_FOR_REVIEW = 'assessment:submit_for_review';
export const ASSESSMENT_REVIEW = 'assessment:review';
export const ASSESSMENT_SUBMIT_FOR_APPROVAL = 'assessment:submit_for_approval';
export const ASSESSMENT_APPROVE = 'assessment:approve';
export const ASSESSMENT_REJECT = 'assessment:reject';

// ── Report-level capabilities ──
export const REPORT_VIEW = 'report:view';
export const REPORT_DOWNLOAD = 'report:download';
/** Org-admin: upload / manage report templates at the organisation level. */
export const REPORT_MANAGE_ORG = 'report:manage_org';

// ── Test-level capabilities ──
export const TEST_CONFIGURE = 'test:configure';
export const TEST_RUN = 'test:run';

// ── Navigation / UI-visibility capabilities ──
export const ORG_VIEW_ASSURANCE = 'org:view_assurance';
export const ORG_VIEW_GOVERNANCE = 'org:view_governance';

// ── Project-scoped activity log ──
/** View the activity log for a specific project (project members + elevated org roles). */
export const PROJECT_VIEW_ACTIVITY_LOG = 'project:view_activity_log';

/**
 * All built-in capabilities. Seeded into CapabilityRegistry on first run.
 * Custom capabilities added via the UI are stored only in the registry.
 */
export const ALL_CAPABILITIES = [
  PLATFORM_MANAGE_SYSTEM_ADMINS,
  PLATFORM_MANAGE_ORGANIZATIONS,
  PLATFORM_MANAGE_SYSTEM_POLICIES,
  ORG_MANAGE_MEMBERS,
  ORG_MANAGE_POLICIES,
  ORG_MANAGE_SETTINGS,
  ORG_VIEW_ACTIVITY_LOG,
  ORG_CREATE_PROJECT,
  ORG_VIEW_ALL_PROJECTS,
  PROJECT_MANAGE_MEMBERS,
  PROJECT_ARCHIVE,
  PROJECT_MANAGE_ASSESSMENTS,
  ASSESSMENT_ANSWER,
  ASSESSMENT_SUBMIT_FOR_REVIEW,
  ASSESSMENT_REVIEW,
  ASSESSMENT_SUBMIT_FOR_APPROVAL,
  ASSESSMENT_APPROVE,
  ASSESSMENT_REJECT,
  REPORT_VIEW,
  REPORT_DOWNLOAD,
  REPORT_MANAGE_ORG,
  TEST_CONFIGURE,
  TEST_RUN,
  ORG_VIEW_ASSURANCE,
  ORG_VIEW_GOVERNANCE,
  PROJECT_VIEW_ACTIVITY_LOG,
] as const;

export type Capability = (typeof ALL_CAPABILITIES)[number];
